"use client";

import { useCallback, useEffect, useState } from "react";
import Link from "next/link";
import { formatCents } from "@/lib/pricing/calculateQuote";
import { addDaysIso, todayIsoPhoenix } from "@/lib/format/date";
import type { YardSize } from "@/lib/types";

type QuoteResult = {
  id: string;
  outcome: string;
  frequency?: string;
  recurring_total_cents: number;
  initial_cleanup_cents?: number | null;
  catch_up_included_minutes?: number | null;
  rush_surcharge_cents?: number | null;
  line_items: { key: string; label: string; amount_cents: number }[];
  service_day?: number | null;
  desired_start_date?: string | null;
  message: string;
  signup_eligible: boolean;
};

type YardEstimate = {
  suggested_yard_size: YardSize;
  suggested_label: string;
  estimated_dog_area_sqft: number | null;
  parcel: { land_size_sqft: number | null; physical_address: string } | null;
  note: string;
};

const FREQ_LABEL: Record<string, string> = {
  weekly: "Weekly",
  biweekly: "Every other week",
  twice_weekly: "Twice weekly",
};

/** Maps “not sure” UI choice to standard (medium) for quoting. */
type YardUiChoice = YardSize | "not_sure";

const YARD_UI_OPTIONS: {
  value: YardUiChoice;
  title: string;
  detail: string;
}[] = [
  {
    value: "medium",
    title: "Typical fenced backyard",
    detail: "Most West Valley tract-home yards",
  },
  {
    value: "large",
    title: "Larger-than-average backyard",
    detail: "Noticeably more area to search than a typical yard",
  },
  {
    value: "extra_large",
    title: "Very large backyard",
    detail: "Extensive outdoor dog area",
  },
  {
    value: "oversized",
    title: "Acreage or multiple separated areas",
    detail: "Custom quote after review",
  },
  {
    value: "not_sure",
    title: "Not sure",
    detail: "We’ll estimate from your address and confirm on the first visit",
  },
];

function yardUiToSize(choice: YardUiChoice): YardSize {
  return choice === "not_sure" ? "medium" : choice;
}

function yardSizeLabel(size: YardSize): string {
  return (
    YARD_UI_OPTIONS.find((o) => o.value === size)?.title ??
    (size === "medium" ? "Typical fenced backyard" : size)
  );
}

export function QuoteForm({
  submitLabel = "Get my exact quote",
}: {
  submitLabel?: string;
}) {
  const [loading, setLoading] = useState(false);
  const [estimating, setEstimating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<QuoteResult | null>(null);
  const [yardSize, setYardSize] = useState<YardSize>("medium");
  const [yardUi, setYardUi] = useState<YardUiChoice>("medium");
  const [estimate, setEstimate] = useState<YardEstimate | null>(null);
  const [yardConfirmed, setYardConfirmed] = useState(false);
  // Date bounds set client-side only (SSR clock/TZ can disagree with the browser).
  const [startMin, setStartMin] = useState("");
  const [startDefault, setStartDefault] = useState("");

  useEffect(() => {
    const today = todayIsoPhoenix();
    setStartMin(today);
    setStartDefault(addDaysIso(today, 7));
  }, []);

  function selectYard(choice: YardUiChoice) {
    setYardUi(choice);
    setYardSize(yardUiToSize(choice));
    setYardConfirmed(true);
  }

  const runYardEstimate = useCallback(async (form: HTMLFormElement) => {
    const fd = new FormData(form);
    const address_line1 = String(fd.get("address_line1") ?? "").trim();
    const city = String(fd.get("city") ?? "").trim();
    const postal_code = String(fd.get("postal_code") ?? "").trim();
    if (address_line1.length < 5 || postal_code.length < 5) return;

    setEstimating(true);
    try {
      const res = await fetch("/api/property/yard-estimate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          address_line1,
          city,
          state: "AZ",
          postal_code,
        }),
      });
      const data = await res.json();
      if (!res.ok) return;
      setEstimate(data);
      setYardSize(data.suggested_yard_size as YardSize);
      setYardUi(data.suggested_yard_size as YardSize);
      setYardConfirmed(false);
    } catch {
      // optional assist — form still works manually
    } finally {
      setEstimating(false);
    }
  }, []);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const fd = new FormData(e.currentTarget);
    const payload = {
      address_line1: String(fd.get("address_line1")),
      city: String(fd.get("city")),
      state: String(fd.get("state") || "AZ"),
      postal_code: String(fd.get("postal_code")),
      dog_count: Number(fd.get("dog_count")),
      yard_size: yardSize,
      frequency: String(fd.get("frequency")),
      last_cleaned: String(fd.get("last_cleaned")),
      gate_access: String(fd.get("gate_access")),
      dogs_remain_outside: false,
      trash_cart_available: fd.get("trash_cart_available") === "on",
      desired_start_date: String(fd.get("desired_start_date")),
      phone: String(fd.get("phone")),
      email: String(fd.get("email")),
    };
    try {
      const res = await fetch("/api/quotes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error?.message ?? "Quote failed");
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error");
    } finally {
      setLoading(false);
    }
  }

  if (result) {
    const catchUpTotal = result.initial_cleanup_cents;
    const rush = result.rush_surcharge_cents ?? 0;
    // Catch-up amount is the first-cleanup TOTAL (not stacked on recurring).
    const firstVisitBase =
      catchUpTotal != null && catchUpTotal > 0
        ? catchUpTotal
        : result.recurring_total_cents;
    const firstVisit = firstVisitBase + rush;

    return (
      <div className="rounded-lg border border-[var(--line)] bg-[var(--panel)] p-6 shadow-sm">
        <p className="text-sm uppercase tracking-wide text-[var(--muted)]">
          {result.outcome === "outside_area" || result.outcome === "waitlisted"
            ? "Not currently available"
            : result.outcome === "zone_surcharge"
              ? "Available with location adjustment"
              : "Available"}
        </p>
        <p className="mt-3 text-base">{result.message}</p>
        {result.outcome !== "outside_area" &&
          result.outcome !== "waitlisted" && (
            <p className="mt-2 text-sm text-[var(--muted)]">
              Next step: choose the weekday(s) that work for you. Exact arrival
              time is estimated the evening before each visit—we text when
              we&apos;re on the way.
            </p>
          )}
        {result.desired_start_date &&
          result.outcome !== "outside_area" &&
          result.outcome !== "waitlisted" && (
            <p className="mt-1 text-sm text-[var(--muted)]">
              Requested start: {result.desired_start_date}
            </p>
          )}
        {(result.outcome === "outside_area" ||
          result.outcome === "waitlisted") && (
          <div className="mt-6 flex flex-wrap gap-3">
            <p className="text-sm text-[var(--muted)]">
              We saved your details. We&apos;ll contact you as the route expands.
            </p>
            <button
              type="button"
              className="rounded-md border border-[var(--line)] px-4 py-2"
              onClick={() => setResult(null)}
            >
              Check another address
            </button>
          </div>
        )}
        {result.outcome !== "outside_area" &&
          result.outcome !== "waitlisted" && (
            <>
              <dl className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-[var(--muted)]">Due today</dt>
                  <dd className="font-medium">{formatCents(0)}</dd>
                </div>
                <div className="flex justify-between text-base">
                  <dt>
                    {catchUpTotal != null && catchUpTotal > 0
                      ? "First cleanup total"
                      : "First visit total"}
                  </dt>
                  <dd className="font-semibold">{formatCents(firstVisit)}</dd>
                </div>
                <div className="flex justify-between text-base">
                  <dt>Ongoing visit price</dt>
                  <dd className="font-semibold">
                    {formatCents(result.recurring_total_cents)}
                  </dd>
                </div>
              </dl>
              {result.frequency && (
                <p className="mt-2 text-sm text-[var(--muted)]">
                  {FREQ_LABEL[result.frequency] ?? result.frequency} service ·
                  per completed visit
                </p>
              )}
              {catchUpTotal != null && catchUpTotal > 0 && (
                <p className="mt-2 text-sm">
                  First cleanup total {formatCents(catchUpTotal)}
                  {result.catch_up_included_minutes
                    ? ` (up to ${result.catch_up_included_minutes} minutes)`
                    : ""}
                  . Your regular recurring rate begins with the following
                  scheduled visit. Extra time only with your approval.
                </p>
              )}
              {rush > 0 && (
                <p className="mt-2 text-sm">
                  Rush scheduling surcharge from +{formatCents(rush)}, added to
                  the cleanup price above. Subject to route availability — not
                  confirmed until you hear from us.
                </p>
              )}
              <p className="mt-2 text-xs text-[var(--muted)]">
                Charged after each completed visit. No contract.
              </p>
              <ul className="mt-4 space-y-1 text-sm">
                {result.line_items.map((i) => (
                  <li key={i.key} className="flex justify-between">
                    <span>{i.label}</span>
                    <span>{formatCents(i.amount_cents)}</span>
                  </li>
                ))}
                {catchUpTotal != null && catchUpTotal > 0 && (
                  <li className="flex justify-between font-medium">
                    <span>First cleanup total (replaces first visit)</span>
                    <span>{formatCents(catchUpTotal)}</span>
                  </li>
                )}
                {rush > 0 && (
                  <li className="flex justify-between">
                    <span>Rush scheduling surcharge (from)</span>
                    <span>+{formatCents(rush)}</span>
                  </li>
                )}
              </ul>
              <div className="mt-6 flex flex-wrap gap-3">
                {result.signup_eligible ? (
                  <Link
                    href={`/signup?quote=${result.id}`}
                    className="rounded-md bg-[var(--accent)] px-4 py-2 text-white"
                  >
                    Continue — choose your service day
                  </Link>
                ) : (
                  <p className="text-sm text-[var(--muted)]">
                    We saved your quote. We will reach out when your route opens.
                  </p>
                )}
                <button
                  type="button"
                  className="rounded-md border border-[var(--line)] px-4 py-2"
                  onClick={() => setResult(null)}
                >
                  New quote
                </button>
              </div>
            </>
          )}
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="grid gap-4 rounded-lg border border-[var(--line)] bg-[var(--panel)] p-6 shadow-sm md:grid-cols-2"
    >
      <label className="grid gap-1 text-sm md:col-span-2">
        Street address
        <input
          name="address_line1"
          required
          className="rounded border border-[var(--line)] px-3 py-2"
          onBlur={(e) => {
            const form = e.currentTarget.form;
            if (form) void runYardEstimate(form);
          }}
        />
      </label>
      <label className="grid gap-1 text-sm">
        City
        <input
          name="city"
          required
          defaultValue="Goodyear"
          className="rounded border border-[var(--line)] px-3 py-2"
          onBlur={(e) => {
            const form = e.currentTarget.form;
            if (form) void runYardEstimate(form);
          }}
        />
      </label>
      <label className="grid gap-1 text-sm">
        ZIP code
        <input
          name="postal_code"
          required
          defaultValue="85338"
          className="rounded border border-[var(--line)] px-3 py-2"
          onBlur={(e) => {
            const form = e.currentTarget.form;
            if (form) void runYardEstimate(form);
          }}
        />
      </label>
      <input type="hidden" name="state" value="AZ" />
      <label className="grid gap-1 text-sm">
        Number of dogs
        <input
          name="dog_count"
          type="number"
          min={1}
          defaultValue={1}
          required
          className="rounded border border-[var(--line)] px-3 py-2"
        />
      </label>

      <div className="grid gap-2 text-sm md:col-span-2">
        <span className="font-medium">
          Which best describes the area your dogs use?
        </span>
        {estimating && (
          <p className="text-xs text-[var(--muted)]">
            Checking public property records…
          </p>
        )}
        {estimate && (
          <div className="rounded-md border border-[var(--line)] bg-[var(--bg)] px-3 py-3">
            <p>
              Based on your address, this looks like a{" "}
              <strong>{estimate.suggested_label}</strong> yard.
            </p>
            <p className="mt-1 text-xs text-[var(--muted)]">{estimate.note}</p>
            <p className="mt-2 text-sm">
              Is that accurate for the outdoor areas your dogs use?
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                className="rounded-md border border-[var(--line)] px-3 py-1.5 text-sm hover:bg-[var(--panel)]"
                onClick={() => selectYard(estimate.suggested_yard_size)}
              >
                Yes, {estimate.suggested_label}
              </button>
              <button
                type="button"
                className="rounded-md border border-[var(--line)] px-3 py-1.5 text-sm hover:bg-[var(--panel)]"
                onClick={() => selectYard("medium")}
              >
                My dog area is smaller
              </button>
              <button
                type="button"
                className="rounded-md border border-[var(--line)] px-3 py-1.5 text-sm hover:bg-[var(--panel)]"
                onClick={() =>
                  selectYard(
                    estimate.suggested_yard_size === "medium"
                      ? "large"
                      : estimate.suggested_yard_size === "large"
                        ? "extra_large"
                        : "oversized",
                  )
                }
              >
                My dog area is larger
              </button>
              <button
                type="button"
                className="rounded-md border border-[var(--line)] px-3 py-1.5 text-sm hover:bg-[var(--panel)]"
                onClick={() => selectYard("not_sure")}
              >
                I&apos;m not sure
              </button>
            </div>
            {yardConfirmed && (
              <p className="mt-2 text-xs text-[var(--muted)]">
                Using:{" "}
                {yardUi === "not_sure"
                  ? "Not sure — we’ll confirm on the first visit"
                  : yardSizeLabel(yardSize)}
              </p>
            )}
          </div>
        )}
        <div className="grid gap-2 sm:grid-cols-2">
          {YARD_UI_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className={`flex cursor-pointer flex-col gap-0.5 rounded-md border px-3 py-2 ${
                yardUi === opt.value
                  ? "border-[var(--accent)] bg-[var(--bg)]"
                  : "border-[var(--line)]"
              }`}
            >
              <span className="flex items-center gap-2">
                <input
                  type="radio"
                  name="yard_size_ui"
                  checked={yardUi === opt.value}
                  onChange={() => selectYard(opt.value)}
                />
                <span className="font-medium">{opt.title}</span>
              </span>
              <span className="pl-6 text-xs text-[var(--muted)]">
                {opt.detail}
              </span>
            </label>
          ))}
        </div>
        <p className="text-xs text-[var(--muted)]">
          Final pricing is based on the actual outdoor areas your dogs use. Your
          exact price is shown before signup and will never increase without
          your approval.
        </p>
      </div>

      <label className="grid gap-1 text-sm">
        Cleanup frequency
        <select
          name="frequency"
          className="rounded border border-[var(--line)] px-3 py-2"
        >
          <option value="twice_weekly">Twice weekly — from $17.99/visit</option>
          <option value="weekly">Weekly — from $19.99/visit</option>
          <option value="biweekly">Every other week — from $29.99/visit</option>
        </select>
      </label>
      <label className="grid gap-1 text-sm">
        Last time yard cleaned
        <select
          name="last_cleaned"
          className="rounded border border-[var(--line)] px-3 py-2"
        >
          <option value="within_week">Within 7 days</option>
          <option value="1_2_weeks">8–14 days</option>
          <option value="15_30_days">15–30 days</option>
          <option value="1_2_months">1–2 months</option>
          <option value="1_month_plus">More than 2 months</option>
          <option value="never">Never</option>
          <option value="unknown">Unknown</option>
        </select>
        <span className="text-xs text-[var(--muted)]">
          When a catch-up is needed, the amount shown is your first cleanup
          total—not an add-on.
        </span>
      </label>
      <label className="grid gap-1 text-sm md:col-span-2">
        Gate access
        <input
          name="gate_access"
          placeholder="Side gate on left"
          className="rounded border border-[var(--line)] px-3 py-2"
        />
      </label>
      <label className="flex flex-col gap-1 text-sm md:col-span-2">
        <span className="flex items-center gap-2">
          <input name="trash_cart_available" type="checkbox" defaultChecked />{" "}
          Outdoor trash cart available
        </span>
        <span className="text-xs text-[var(--muted)]">
          Waste is double-bagged into your cart when available. Uncheck for
          haul-away (+$5 per recurring visit). First/catch-up cleanups include
          haul-away.
        </span>
      </label>
      <label className="grid gap-1 text-sm">
        Desired start date
        <input
          name="desired_start_date"
          type="date"
          required
          min={startMin || undefined}
          value={startDefault}
          onChange={(e) => setStartDefault(e.target.value)}
          className="rounded border border-[var(--line)] px-3 py-2"
        />
        <span className="text-xs text-[var(--muted)]">
          Same-day adds a rush surcharge from +$39 on top of the cleanup price,
          subject to route availability.
        </span>
      </label>
      <label className="grid gap-1 text-sm">
        Phone
        <input
          name="phone"
          required
          placeholder="+16025551212"
          className="rounded border border-[var(--line)] px-3 py-2"
        />
      </label>
      <label className="grid gap-1 text-sm md:col-span-2">
        Email
        <input
          name="email"
          type="email"
          required
          className="rounded border border-[var(--line)] px-3 py-2"
        />
      </label>
      {error && (
        <p className="text-sm text-[var(--danger)] md:col-span-2">{error}</p>
      )}
      <button
        type="submit"
        disabled={loading}
        className="rounded-md bg-[var(--accent)] px-4 py-3 font-medium text-white md:col-span-2"
      >
        {loading ? "Checking…" : submitLabel}
      </button>
    </form>
  );
}
